import * as monaco from 'monaco-editor';
import parserBabel from 'prettier/plugins/babel';
import prettierPluginEstree from 'prettier/plugins/estree';
import prettier from 'prettier/standalone';
import prettierPluginJava from 'prettier-plugin-java';
import { must } from './dom.ts';

/*
 * Install prettier-java as our code formatter for Java.
 */
monaco.languages.registerDocumentFormattingEditProvider('java', {
  async provideDocumentFormattingEdits(model) {
    const text = await prettier.format(model.getValue(), {
      parser: 'java',
      plugins: [prettierPluginJava],
      tabWidth: 2,
    });
    const range = model.getFullModelRange();
    return [{ range, text }];
  },
});

/*
 * Install prettier-babel as our code formatter for Javascript.
 */
monaco.languages.registerDocumentFormattingEditProvider('javascript', {
  async provideDocumentFormattingEdits(model) {
    const text = await prettier.format(model.getValue(), {
      parser: 'babel',
      plugins: [parserBabel, prettierPluginEstree],
      tabWidth: 2,
    });
    const range = model.getFullModelRange();
    return [{ range, text }];
  },
});

/*
 * Monaco internally cancels in-flight async work when the editor's model
 * changes (e.g. switching tabs) and lets the resulting "Canceled" promise
 * rejection escape as an unhandled rejection. It's harmless noise; swallow it
 * so it doesn't clutter the console (or scare students).
 */
window.addEventListener('unhandledrejection', (e) => {
  if (e.reason?.name === 'Canceled' && e.reason?.message === 'Canceled') {
    e.preventDefault();
  }
});

window.MonacoEnvironment = {
  // This seems like a kludge. Maybe it's good. What is the difference between
  // good and bad really? This seems to account for the case that we've set a
  // <base> tag in our HTML and if we didn't has no effect so that seems fine.
  baseUrl: document.body.baseURI,

  getWorkerUrl(_moduleId, label) {
    switch (label) {
      case 'json':
        return '/js/vs/language/json/json.worker.js';
      case 'css':
      case 'scss':
      case 'less':
        return '/js/vs/language/css/css.worker.js';
      case 'html':
      case 'handlebars':
      case 'razor':
        return '/js/vs/language/html/html.worker.js';
      case 'typescript':
      case 'javascript':
        return '/js/vs/language/typescript/ts.worker.js';
      default:
        return '/js/vs/editor/editor.worker.js';
    }
  },
};

/*
 * Follow the site theme (plans/dark-mode.md): vs in light, vs-dark in dark.
 * theme-boot.js stamps data-theme before any module runs and dispatches
 * bhs:themechange on later flips; setTheme is a Monaco global, so one
 * listener covers every editor on the page.
 */
const monacoTheme = () =>
  document.documentElement.getAttribute('data-theme') === 'dark' ? 'vs-dark' : 'vs';

document.addEventListener('bhs:themechange', () => monaco.editor.setTheme(monacoTheme()));

// Options to disable intellisense from
// https://github.com/microsoft/monaco-editor/issues/1681
const disableIntellisense: monaco.editor.IStandaloneEditorConstructionOptions = {
  quickSuggestions: {
    other: false,
    comments: false,
    strings: false,
  },
  parameterHints: { enabled: false },
  suggestOnTriggerCharacters: false,
  acceptSuggestionOnEnter: 'off',
  tabCompletion: 'off',
  wordBasedSuggestions: 'off',
};

const editor = (id: string, language: string | undefined) => {
  const div = must(document.getElementById(id), `editor element #${id}`);
  const e = monaco.editor.create(div, {
    language,
    theme: monacoTheme(),
    automaticLayout: true,
    tabSize: 2,
    ...disableIntellisense,
  });

  // This seems to be the way to make the editor resize when the window resizes.
  const fixLayout = () => {
    const container = e.getContainerDomNode();

    // Force the container to recalculate its size
    const originalDisplay = container.style.display;
    container.style.display = 'none';
    container.offsetHeight; // Trigger reflow
    container.style.display = originalDisplay;

    // Not clear this actually works. But the old hack of laying it out to
    // width:0, height: 0 stopped working.
    e.layout();
  };

  // Normal window resizes
  window.addEventListener('resize', fixLayout);

  // Kludge custom event we can use when, e.g. we collapse and rexpand instructions,
  window.addEventListener('reflowPlease', fixLayout);

  return e;
};

export default editor;
